import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Wind, LogOut } from "lucide-react";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["Overview", "Orders", "Leads", "Buy clicks", "Supplier"];
const FULFILLMENT = ["unfulfilled", "shipped", "delivered", "cancelled"];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [supplierStatus, setSupplierStatus] = useState(null);
  const [supplierJobs, setSupplierJobs] = useState([]);

  useEffect(() => {
    if (user === null) nav("/auth");
  }, [user, nav]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const opts = { withCredentials: true };
    axios.get(`${API}/admin/stats`, opts).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/orders`, opts).then((r) => setOrders(r.data)).catch(() => {});
    axios.get(`${API}/admin/leads`, opts).then((r) => setLeads(r.data)).catch(() => {});
    axios.get(`${API}/admin/clicks`, opts).then((r) => setClicks(r.data)).catch(() => {});
    axios.get(`${API}/admin/supplier/status`, opts).then((r) => setSupplierStatus(r.data)).catch(() => {});
    axios.get(`${API}/admin/supplier/jobs`, opts).then((r) => setSupplierJobs(r.data)).catch(() => {});
  }, [user]);

  const refreshSupplier = () => {
    const opts = { withCredentials: true };
    axios.get(`${API}/admin/supplier/status`, opts).then((r) => setSupplierStatus(r.data)).catch(() => {});
    axios.get(`${API}/admin/supplier/jobs`, opts).then((r) => setSupplierJobs(r.data)).catch(() => {});
  };

  const retryJob = async (orderId) => {
    await axios.post(`${API}/admin/supplier/jobs/${orderId}/retry`, {}, { withCredentials: true });
    refreshSupplier();
  };

  const connectAli = async () => {
    const { data } = await axios.get(`${API}/aliexpress/oauth/start`, { withCredentials: true });
    window.location.assign(data.authorize_url);
  };

  if (!user) return null;
  if (user.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-denied">
        <div className="border border-steel bg-carbon p-10 text-center">
          <p className="font-display text-xl font-bold uppercase">Admins only</p>
          <p className="mt-2 text-sm text-smoke">This account doesn't have dashboard access.</p>
          <Link to="/" className="mt-6 inline-block border border-volt px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] text-volt hover:bg-volt hover:text-ink">Back to store</Link>
        </div>
      </div>
    );
  }

  const setFulfillment = async (orderId, status) => {
    await axios.patch(`${API}/admin/orders/${orderId}/status`, { fulfillment_status: status }, { withCredentials: true });
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, fulfillment_status: status } : o)));
  };

  const statCards = stats
    ? [
        ["Revenue", `$${stats.revenue.toFixed(2)}`],
        ["Orders", stats.orders],
        ["Fans sold", stats.units],
        ["Customers", stats.customers],
        ["Drop-list leads", stats.leads],
        ["Buy clicks", stats.buy_clicks],
      ]
    : [];

  return (
    <div className="relative min-h-screen bg-ink" data-testid="admin-page">
      <header className="relative z-10 border-b border-steel">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest" data-testid="admin-brand">
            <Wind className="h-5 w-5 text-volt" strokeWidth={2.5} />
            <span>X8<span className="text-volt">/</span>HQ</span>
          </Link>
          <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-volt">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-12 md:py-16">
        <h1 className="font-display text-3xl font-black uppercase tracking-tight md:text-5xl">Store <span className="text-volt">HQ</span></h1>

        <div className="mt-8 flex flex-wrap border border-steel font-mono text-xs uppercase tracking-[0.2em]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase().replace(" ", "-")}`}
              onClick={() => setTab(t)}
              className={`px-6 py-3 transition-colors ${tab === t ? "bg-volt font-bold text-ink" : "text-smoke hover:text-volt"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="admin-stats">
            {statCards.map(([k, v]) => (
              <div key={k} className="border border-steel bg-carbon p-6">
                <div className="font-mono text-2xl font-bold text-volt md:text-3xl">{v}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">{k}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "Orders" && (
          <div className="mt-4 border border-steel bg-carbon" data-testid="admin-orders">
            {orders.length === 0 && <p className="p-8 font-mono text-xs text-smoke">No orders yet.</p>}
            {orders.map((o, i) => (
              <div key={o.id} data-testid={`admin-order-${i}`} className="flex flex-wrap items-center justify-between gap-4 border-b border-steel px-6 py-4 last:border-b-0">
                <div>
                  <div className="font-mono text-sm font-bold text-volt">{o.id}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">
                    {o.bundle_label} · ${o.amount?.toFixed(2)} · {o.customer_email} · via {o.provider}
                  </div>
                  {o.shipping?.line1 && (
                    <div className="mt-1 font-mono text-[10px] text-smoke">
                      → {o.shipping.name}, {o.shipping.line1}, {o.shipping.city} {o.shipping.state} {o.shipping.postal_code}, {o.shipping.country}
                    </div>
                  )}
                </div>
                <select
                  data-testid={`admin-order-status-${i}`}
                  value={o.fulfillment_status}
                  onChange={(e) => setFulfillment(o.id, e.target.value)}
                  className="border border-steel bg-ink px-3 py-2 font-mono text-xs uppercase text-foreground focus:border-volt focus:outline-none"
                >
                  {FULFILLMENT.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "Leads" && (
          <div className="mt-4 border border-steel bg-carbon" data-testid="admin-leads">
            {leads.length === 0 && <p className="p-8 font-mono text-xs text-smoke">No leads yet.</p>}
            {leads.map((l, i) => (
              <div key={l.email} data-testid={`admin-lead-${i}`} className="flex justify-between border-b border-steel px-6 py-4 font-mono text-xs last:border-b-0">
                <span>{l.email}</span>
                <span className="text-smoke">{new Date(l.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "Buy clicks" && (
          <div className="mt-4 border border-steel bg-carbon" data-testid="admin-clicks">
            {clicks.length === 0 && <p className="p-8 font-mono text-xs text-smoke">No clicks tracked yet.</p>}
            {clicks.map((c, i) => (
              <div key={i} data-testid={`admin-click-${i}`} className="flex justify-between border-b border-steel px-6 py-4 font-mono text-xs last:border-b-0">
                <span className="text-volt">{c.source}</span>
                <span className="text-smoke">{new Date(c.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "Supplier" && (
          <div className="mt-4" data-testid="admin-supplier">
            <div className="border border-steel bg-carbon p-6 md:p-8">
              <h2 className="font-display text-sm font-bold uppercase tracking-wider">Supplier auto-fulfillment</h2>
              {supplierStatus && (
                <div className="mt-5 grid gap-2 font-mono text-xs">
                  <div className="flex justify-between border-b border-steel pb-2">
                    <span className="text-smoke">CJ Dropshipping API key</span>
                    <span className={supplierStatus.cj_configured ? "text-green-400" : "text-red-400"} data-testid="cj-configured">
                      {supplierStatus.cj_configured ? "SET" : "NOT SET"}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-steel pb-2">
                    <span className="text-smoke">CJ product variant (VID)</span>
                    <span className={supplierStatus.cj_product_vid ? "text-volt" : "text-red-400"} data-testid="cj-vid">
                      {supplierStatus.cj_product_vid || "NOT SET"}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-steel pb-2">
                    <span className="text-smoke">AliExpress (backup path)</span>
                    <span className={supplierStatus.authorized ? "text-green-400" : "text-smoke"} data-testid="supplier-authorized">
                      {supplierStatus.authorized ? "CONNECTED" : supplierStatus.configured ? "KEYS SET — NOT CONNECTED" : "NOT SET"}
                    </span>
                  </div>
                </div>
              )}
              {!supplierStatus?.cj_configured && (
                <div className="mt-6 text-sm leading-relaxed text-smoke" data-testid="supplier-setup-steps">
                  <p className="font-bold text-foreground">To activate CJ auto-fulfillment:</p>
                  <ol className="mt-3 list-decimal space-y-2 pl-5">
                    <li>Create a free account at <span className="text-volt">cjdropshipping.com</span></li>
                    <li>Go to <span className="text-volt">My CJ → Authorization → API</span> and create an API key</li>
                    <li>Send me the API key — I'll wire it in (orders are created unpaid by default; you approve payment inside CJ until we switch on auto-charge)</li>
                    <li>Find the X8 fan on CJ (or request free sourcing with your AliExpress link) and send me the CJ product link — I'll set the variant ID</li>
                  </ol>
                </div>
              )}
              {supplierStatus?.cj_configured && !supplierStatus?.cj_product_vid && (
                <p className="mt-6 text-sm text-smoke">API key connected. Last step: send me the CJ product link for the X8 fan so I can set the variant ID.</p>
              )}
              {supplierStatus?.configured && !supplierStatus?.authorized && (
                <button data-testid="ali-connect-button" onClick={connectAli} className="mt-6 border border-steel px-8 py-3 font-mono text-xs tracking-[0.2em] uppercase text-smoke transition-colors hover:border-volt hover:text-volt">
                  Connect AliExpress (backup)
                </button>
              )}
              <p className="mt-6 font-mono text-[10px] tracking-[0.15em] uppercase text-smoke">
                Every paid order auto-queues below. Failed attempts retry with backoff, then land in manual review — no order is ever lost.
              </p>
            </div>

            <div className="mt-4 border border-steel bg-carbon" data-testid="supplier-jobs">
              {supplierJobs.length === 0 && <p className="p-8 font-mono text-xs text-smoke">No fulfillment jobs yet.</p>}
              {supplierJobs.map((j, i) => (
                <div key={j.order_id} data-testid={`supplier-job-${i}`} className="flex flex-wrap items-center justify-between gap-4 border-b border-steel px-6 py-4 last:border-b-0">
                  <div>
                    <span className="font-mono text-sm font-bold text-volt">{j.order_id}</span>
                    <span className={`ml-4 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${
                      j.status === "done" ? "border-green-400 text-green-400"
                      : j.status === "manual_review" ? "border-red-400 text-red-400"
                      : "border-steel text-smoke"
                    }`} data-testid={`supplier-job-status-${i}`}>{j.status.replace("_", " ")}</span>
                    {j.error && <div className="mt-1 max-w-xl font-mono text-[10px] text-red-400">{j.error}</div>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-[10px] text-smoke">attempts: {j.attempts}</span>
                    {(j.status === "manual_review" || j.status === "queued") && (
                      <button data-testid={`supplier-retry-${i}`} onClick={() => retryJob(j.order_id)} className="border border-volt px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-volt transition-colors hover:bg-volt hover:text-ink">
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
